import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { useCompany } from './CompanyContext';
import { startBottomChromeWatch } from '../utils/browserChrome';

// Default brand accent (Rich Imperial Gold), used when neither the user nor the
// company has set one. Pairs with a near-black companion text color so labels
// on accent fills read as black-on-gold (the platform default look).
const DEFAULT_ACCENT_COLOR = '#b8860b';
// Default text color ON accent-filled elements (near-black) — renders on top of
// the gold accent fill (button labels, active pipeline stage names, kanban
// header text). Black-on-gold is the platform default pairing.
const DEFAULT_ACCENT_TEXT_COLOR = '#1a1a1a';
// Default trailing (past-stage) color — the dark "done" fill previously
// hardcoded as #4a4a4a day / #3a3a3a night on pipeline visuals. Now a single
// per-user color used in both modes.
const DEFAULT_TRAILING_COLOR = '#4a4a4a';

// ── THE ACCENT, REMEMBERED ACROSS RELOADS ───────────────────────────────────
//
// The resolved accent pair, cached in localStorage exactly the way
// `boneflow_theme` caches the day/night mode and for exactly the same reason:
// it is needed on the FIRST PAINT, and its real source (the user document) is
// an async round trip away.
//
// WHAT THIS FIXES. App.js's RouteSplash spinner reads accentColor, so it is
// "the user's colour" — but on a cold load nothing has answered yet:
// userProfile is null, company is null, and the resolution chain falls all the
// way through to the platform gold. Measured at 375px against a QA account
// whose personal accent is teal: the splash at "/" painted rgb(184,134,11) at
// t=24ms (gold — the default), while the LATER /dashboard chunk splash, which
// happens after ProtectedRoute has waited for the profile, painted
// rgb(47,111,143) (the real accent). Same component, two moments; the earlier
// fix taught the spinner to READ the accent without making the accent
// AVAILABLE that early.
//
// It is a HINT, never an authority — the same standing as utils/sessionHint.js.
// Nothing is gated on it, a stale value costs one spinner's worth of the wrong
// colour, and it is cleared the moment auth reports nobody is signed in, so a
// signed-out device does not keep painting the last user's colour. A brand-new
// user's very first load has no cache and gets the platform default, which is
// correct.
//
// Every access is wrapped: localStorage THROWS (rather than returning null)
// when storage is disabled or in some Safari private-mode configurations, and
// this runs during provider construction — an unguarded throw here is a white
// screen for the whole app.
const ACCENT_CACHE_KEY = 'boneflow_accent';
const ACCENT_TEXT_CACHE_KEY = 'boneflow_accent_text';

function readCachedAccents() {
  try {
    return {
      accent: localStorage.getItem(ACCENT_CACHE_KEY) || null,
      accentText: localStorage.getItem(ACCENT_TEXT_CACHE_KEY) || null,
    };
  } catch (e) {
    return { accent: null, accentText: null };
  }
}

const ThemeContext = createContext();

// The phone breakpoint and the minimum tap size, in one place. 480 matches
// Dashboard's `isMobile`, which is the width the app shell swaps its whole
// layout at; 44 is the design system's touch minimum, already used by the
// phone nav drawer, the sidebar rows and the landing page.
export const PHONE_MAX_WIDTH = 480;
export const TOUCH_MIN = 44;

// THE HEADER RULE, IN ONE PLACE. Two separate elements draw what reads as a
// SINGLE continuous 1px line across the top of the app: the sidebar's
// BoneFlow-lockup bottom border, and the top bar's. Nothing tied them together,
// so each landed wherever its own padding plus its own tallest child happened
// to add up to — the sidebar at 24 + 28 + 20 = 72, the top bar at 16 + 41 + 16
// = 73 (the 41 is the "+ New" button) — and the line was measurably 1px broken
// at every width and in both themes.
//
// This is the y, in CSS pixels from the top of the viewport, at which that
// shared rule paints. Both sides derive a min-height from it rather than
// carrying a hand-tuned padding, so a future change to the "+ New" button's
// height moves nothing. Phone-only surfaces (the top bar under a safe-area
// inset, the nav drawer) opt out — there is no sidebar beside them to align to.
export const HEADER_RULE_Y = 73;

export function useTheme() {
  return useContext(ThemeContext);
}

// Three-tier divider system. Use the strongest tier that still feels right
// for the boundary: STRONG between major regions (page header / tab bar /
// sidebar), MEDIUM between distinct list records, SOFT for tightly-related
// rows inside a panel.
export const themes = {
  night: {
    background: '#0e0d0b',
    surface: '#131210',
    surfaceRaised: '#181714',
    surfaceHover: '#1e1c18',
    border: '#242118',
    borderStrong: '#2e2b22',
    borderHover: '#3d3a30',
    text: '#f0ede6',
    textMuted: '#c8c4be',
    textFaint: '#c8c4be',
    textGhost: '#c8c4be',
    accent: '#f0ede6',
    accentText: '#0e0d0b',
    accentHover: '#d4d0c8',
    danger: '#c8554a',
    dangerHover: '#a8443a',
    dangerText: '#f0ede6',
    secondaryBg: '#181714',
    secondaryBgHover: '#1e1c18',
    secondaryBorder: '#3d3a30',
    secondaryText: '#f0ede6',
    mono: "'SF Mono', 'Monaco', 'Consolas', monospace",
    separator: 'rgba(244,241,235,0.15)',
    dividerStrong: '#f4f1eb',
    dividerMedium: '#333333',
    dividerSoft: '#2a2a2a',
  },
  day: {
    background: '#f4f1eb',
    surface: '#faf8f4',
    surfaceRaised: '#f0ede6',
    surfaceHover: '#e8e4da',
    border: '#d4cfc4',
    borderStrong: '#c4bfb4',
    borderHover: '#a09a8e',
    text: '#1a1a1a',
    textMuted: '#3d3d3d',
    textFaint: '#3d3d3d',
    textGhost: '#3d3d3d',
    accent: '#0e0d0b',
    accentText: '#f4f1eb',
    accentHover: '#2a2825',
    danger: '#9c3d35',
    dangerHover: '#7e312a',
    dangerText: '#f4f1eb',
    secondaryBg: '#faf8f4',
    secondaryBgHover: '#e8e4da',
    secondaryBorder: '#c4bfb4',
    secondaryText: '#1a1a1a',
    mono: "'SF Mono', 'Monaco', 'Consolas', monospace",
    separator: '#1a1a1a',
    dividerStrong: '#1a1a1a',
    dividerMedium: '#d0d0d0',
    dividerSoft: '#ebebeb',
  },
};

export function ThemeProvider({ children }) {
  const { userProfile, user, loading: authLoading } = useAuth();
  const { company } = useCompany();
  // Read ONCE, at construction, before any auth work starts — a mid-flight
  // write must not change what this render is painting half way through
  // resolving. Same useState-initializer discipline as RootRoute's session
  // hint. useRef would do as well; useState makes "never re-read" explicit.
  const [bootAccents] = useState(readCachedAccents);
  // New accounts (no saved preference) default to Day mode — the warm off-white
  // experience — matching the index.html boot script's 'day' default. Existing
  // users' saved themeMode still loads via the effect below.
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem('boneflow_theme') || 'day';
    } catch (e) {
      return 'day';
    }
  });

  useEffect(() => {
    const load = async () => {
      if (!userProfile?.uid) return;
      const ref = doc(db, 'users', userProfile.uid);
      const snap = await getDoc(ref);
      if (snap.exists() && snap.data().themeMode) {
        const savedMode = snap.data().themeMode;
        setMode(savedMode);
        localStorage.setItem('boneflow_theme', savedMode);
        document.documentElement.style.backgroundColor = savedMode === 'night' ? '#0e0d0b' : '#f4f1eb';
      }
    };
    load();
    // Key on uid so this loads the saved theme once per user. Keying on the whole
    // userProfile object re-ran it on every user-doc write — including the
    // themeMode write that toggleTheme itself makes — which is wasteful and could
    // fight an in-session toggle.
  }, [userProfile?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── isPhone ────────────────────────────────────────────────────────────
  // "Is this a phone", for the whole app, from a context every component that
  // needs it already consumes.
  //
  // WHY HERE. Touch sizing is needed in leaf components (NotesSection,
  // TaskPanel, JobFinancials, JobDocumentsTab, DocumentPanel) that render on
  // BOTH the Clients page and the Workflows sheet and have no window-width
  // state of their own. The alternatives were threading an isMobile prop down
  // through two different parents — which drifts the moment one of them
  // forgets — or a resize listener per component, which is the same listener
  // written six times. useTheme() is already the one thing all of them call.
  //
  // 480px is the app's OWN definition of a phone (Dashboard's `isMobile`, and
  // the width its layout switches at). Pages that already track their own
  // windowWidth keep doing so; this exists for the components that cannot.
  const [isPhone, setIsPhone] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth < PHONE_MAX_WIDTH : false)
  );
  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth < PHONE_MAX_WIDTH);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Publish --bf-bottom-chrome: how much of the bottom of the viewport is
  // currently under the BROWSER's own toolbar (Safari's, in an ordinary tab —
  // NOT the home indicator, which is env(safe-area-inset-bottom) and a
  // different number). See utils/browserChrome.js for why it is measured
  // rather than derived from lvh/dvh. Started here for the same reason isPhone
  // lives here: it is one fact about the viewport that leaf components need
  // and cannot each own a listener for. 0px everywhere without such a bar, so
  // nothing reads differently on a desktop or a home-screen install.
  useEffect(() => startBottomChromeWatch(), []);

  // Immersive mode: a full-bleed page state (e.g. an Analytics section view)
  // that hides the sidebar to claim the full viewport width. Pages set this on
  // enter and clear it on leave/unmount; the Dashboard shell reads it to drop
  // the sidebar + its left margin. Defaults off so every other page is unaffected.
  const [immersive, setImmersive] = useState(false);

  const toggleTheme = async () => {
    const newMode = mode === 'night' ? 'day' : 'night';
    setMode(newMode);
    localStorage.setItem('boneflow_theme', newMode);
    document.documentElement.style.backgroundColor = newMode === 'night' ? '#0e0d0b' : '#f4f1eb';
    if (userProfile?.uid) {
      const ref = doc(db, 'users', userProfile.uid);
      await updateDoc(ref, { themeMode: newMode });
    }
  };

  // Resolved brand accent: a user's personal accent (set in My Profile ›
  // Appearance) wins; otherwise falls back to the company-wide accent
  // (Settings › Company Accent Color); otherwise — once those have actually
  // ANSWERED — the platform default. This drives active pipeline stage
  // indicators and other accent-colored chrome — distinct from `theme.accent`,
  // which is the mode-based ink/paper swap used for generic buttons/selected
  // states.
  const liveAccent = userProfile?.accentColor || company?.accentColor || null;
  // Companion text color for anything rendered ON an accentColor fill (button
  // labels, the active pipeline stage name, kanban header text). Same
  // resolution chain: personal pick → company default → warm off-white. Kept
  // as a PAIR with the accent through every step below, cache included — a
  // cached accent with a default companion could put near-black text on a dark
  // custom accent.
  const liveAccentText = userProfile?.accentTextColor || company?.accentTextColor || null;
  // The boot cache sits between "nothing has answered" and the platform
  // default, so a returning user's first paint is already their colour instead
  // of gold. See the ACCENT_CACHE_KEY note at the top of this file.
  const accentColor = liveAccent || bootAccents.accent || DEFAULT_ACCENT_COLOR;
  const accentTextColor = liveAccentText || bootAccents.accentText || DEFAULT_ACCENT_TEXT_COLOR;

  // Keep the cache honest. Written only from a REAL resolution (profile or
  // company), and cleared once auth has settled on "nobody" so a signed-out
  // device stops painting the last user's colour. Deliberately not cleared
  // while auth is merely still loading — that is the exact state the cache
  // exists to cover, and clearing there would erase it before it was used.
  useEffect(() => {
    try {
      if (liveAccent) {
        localStorage.setItem(ACCENT_CACHE_KEY, liveAccent);
        if (liveAccentText) localStorage.setItem(ACCENT_TEXT_CACHE_KEY, liveAccentText);
        else localStorage.removeItem(ACCENT_TEXT_CACHE_KEY);
      } else if (!authLoading && !user) {
        localStorage.removeItem(ACCENT_CACHE_KEY);
        localStorage.removeItem(ACCENT_TEXT_CACHE_KEY);
      }
    } catch (e) { /* storage unavailable — the app just boots at the default */ }
  }, [liveAccent, liveAccentText, authLoading, user]);
  // Trailing (past-stage) color: fills past/completed stages on pipeline
  // visuals (StageIndicator, kanban header pipeline) and ZERO-JOB stages on
  // the Dashboard pipeline. Per-user only (no company tier) — a quiet
  // secondary preference set in My Profile › Appearance.
  const trailingColor = userProfile?.trailingColor || DEFAULT_TRAILING_COLOR;

  const value = {
    mode,
    theme: themes[mode],
    toggleTheme,
    isPhone,
    immersive,
    setImmersive,
    accentColor,
    accentTextColor,
    trailingColor,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}